import React, { useContext, useEffect, useState } from "react";
import { NumericFormat } from "react-number-format";
import ExcelJS from "exceljs";
import moment from "moment-timezone";
import { Link } from "react-router-dom";
import * as _ from "lodash-es";

import IOnGetBackorderReportDTO from "@models/DTO/IOnGetBackorderReportDTO";
import IProcessed_Report from "@models/IProcessed_Report";
import IOrder from "@models/IOrder";
import IOwTableCols from "@models/IOwTableCols";
import IErrorDTO from "@models/DTO/IErrorDTO";
import INotification from "@models/INotification";

import SupplierContext from "@contexts/SupplierContext";
import UserContext from "@contexts/UserContext";
import UiContext from "@contexts/UiContext";

import OwTitle from "@components/shared/OwTitle/OwTitle";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import OwTable from "@components/shared/OwTable/OwTable";
import OwButton from "@components/shared/OwButton/OwButton";

import UserService from "../../../../services/UserService";
import UIService from "../../../../services/UIService";

import { Socket } from "socket.io-client";
import { ControlBar } from "@components/shared/ControlBar/ControlBar";

type Props = {
    setActivityIndicator: (activityIndicator: boolean) => void;
    socket: Socket;
    notifyFn: (data: IErrorDTO | null, _notification?: INotification, isFormatted?: boolean) => void;
    processedReport: IProcessed_Report;
};

type ViewType = "byOrder" | "summation";

interface IGroupedItem {
    sku: string;
    name: string;
    totalQty: number;
    totalValue: number;
    orders: {
        orderGuid: string;
        orderName: string;
        vendorName: string;
        qty: number;
        value: number;
    }[];
}

export const BackorderReport: React.FC<Props> = (props) => {
    const supplierModel = useContext(SupplierContext);
    const userModel = useContext(UserContext);
    const uiContext = useContext(UiContext);

    const [viewType, setViewType] = useState<ViewType>("byOrder");
    const [reportData, setReportData] = useState<IOnGetBackorderReportDTO | undefined>(props.processedReport.report_data);
    const [groupedData, setGroupedData] = useState<IGroupedItem[]>([]);
    const [totalItems, setTotalItems] = useState<number>(0);

    useEffect(() => {
        if (reportData) {
            processGroupedData();
        }
    }, [reportData]);

    const processGroupedData = () => {
        if (!reportData) return;

        const itemMap = new Map<string, IGroupedItem>();
        let itemCount = 0;

        for (const order of reportData.orders) {
            if (!order.Order_Items) continue;

            for (const item of order.Order_Items) {
                const qty = item.qty_pending || item.qty || 0;

                itemCount += qty;
                const key = item.Catalog_Item?.guid || item.Catalog_Item?.sku || "";

                if (!itemMap.has(key)) {
                    itemMap.set(key, {
                        sku: item.Catalog_Item?.sku || "",
                        name: item.Catalog_Item?.name || "",
                        totalQty: 0,
                        totalValue: 0,
                        orders: [],
                    });
                }

                const groupedItem = itemMap.get(key)!;
                const itemValue = qty * (item.net_price || 0);

                groupedItem.totalQty += qty;
                groupedItem.totalValue += itemValue;
                groupedItem.orders.push({
                    orderGuid: order.guid || "",
                    orderName: order.name || "",
                    vendorName: order.Vendor?.name || "",
                    qty,
                    value: itemValue,
                });
            }
        }

        setTotalItems(itemCount);
        setGroupedData(Array.from(itemMap.values()).sort((a, b) => b.totalValue - a.totalValue));
    };

    const renderOrderName = (row: Partial<IOrder>) => {
        return (
            <div>
                <Link to={`/orders/${row.guid}/entry`}>{row.name}</Link>
            </div>
        );
    };

    const renderVendorName = (row: Partial<IOrder>) => {
        return row.Vendor?.name || "";
    };

    const renderBackorderValue = (row: Partial<IOrder>) => {
        const value = row.Order_Items?.reduce((sum, item) => sum + (item.qty || 0) * (item.net_price || 0), 0) || 0;

        return (
            <NumericFormat
                value={value}
                displayType={"text"}
                decimalScale={supplierModel.pricing_precision || 2}
                fixedDecimalScale={true}
                thousandSeparator={true}
                prefix="$"
            />
        );
    };

    const renderItemCount = (row: Partial<IOrder>) => {
        return row.Order_Items?.length || 0;
    };

    const renderCreatedDate = (row: Partial<IOrder>) => {
        return UserService.parseLastUpdated(row.created_at);
    };

    const getOrderTableCols = (): IOwTableCols[] => {
        return [
            {
                title: "order",
                valueProp: "name",
                renderFn: renderOrderName,
                renderFnEmitsRow: true,
            },
            {
                title: "account",
                valueProp: "vendor",
                renderFn: renderVendorName,
                renderFnEmitsRow: true,
            },
            {
                title: "items_on_backorder",
                valueProp: "items",
                renderFn: renderItemCount,
                renderFnEmitsRow: true,
                className: "text-center",
            },
            {
                title: "backorder_value",
                valueProp: "value",
                renderFn: renderBackorderValue,
                renderFnEmitsRow: true,
                className: "text-end",
            },
            {
                title: "created_at",
                valueProp: "created_at",
                renderFn: renderCreatedDate,
                renderFnEmitsRow: true,
            },
        ];
    };

    const renderGroupedSku = (row: IGroupedItem) => {
        return row.sku;
    };

    const renderGroupedName = (row: IGroupedItem) => {
        return row.name;
    };

    const renderGroupedQty = (row: IGroupedItem) => {
        return (
            <NumericFormat
                value={row.totalQty}
                displayType={"text"}
                decimalScale={supplierModel.qty_precision || 2}
                fixedDecimalScale={true}
                thousandSeparator={true}
            />
        );
    };

    const renderGroupedValue = (row: IGroupedItem) => {
        return (
            <NumericFormat
                value={row.totalValue}
                displayType={"text"}
                decimalScale={supplierModel.pricing_precision || 2}
                fixedDecimalScale={true}
                thousandSeparator={true}
                prefix="$"
            />
        );
    };

    const renderGroupedOrderCount = (row: IGroupedItem) => {
        return row.orders.length;
    };

    const getGroupedTableCols = (): IOwTableCols[] => {
        return [
            {
                title: "sku",
                valueProp: "sku",
                renderFn: renderGroupedSku,
                renderFnEmitsRow: true,
            },
            {
                title: "name",
                valueProp: "name",
                renderFn: renderGroupedName,
                renderFnEmitsRow: true,
            },
            {
                title: "total_qty",
                valueProp: "totalQty",
                renderFn: renderGroupedQty,
                renderFnEmitsRow: true,
                className: "text-end",
            },
            {
                title: "total_value",
                valueProp: "totalValue",
                renderFn: renderGroupedValue,
                renderFnEmitsRow: true,
                className: "text-end",
            },
            {
                title: "order_count",
                valueProp: "orders",
                renderFn: renderGroupedOrderCount,
                renderFnEmitsRow: true,
                className: "text-center",
            },
        ];
    };

    const downloadExcel = () => {
        if (!reportData) return;

        const workbook = new ExcelJS.Workbook();

        // By Order Tab
        const byOrderWs = workbook.addWorksheet("By Order");
        byOrderWs.columns = [
            { header: "Order", key: "order_name" },
            { header: "Account", key: "account" },
            { header: "Created Date", key: "created_date" },
            { header: "SKU", key: "sku" },
            { header: "Product", key: "product" },
            { header: "Qty", key: "qty" },
            { header: "Price", key: "price" },
            { header: "Total", key: "total" },
        ];

        const headerCells = ["A1", "B1", "C1", "D1", "E1", "F1", "G1", "H1"];
        for (const cell of headerCells) {
            byOrderWs.getCell(cell).font = { bold: true };
        }

        let grandTotalValue = 0;
        let grandTotalItems = 0;

        for (const order of reportData.orders) {
            const orderName = order.name || "";
            const accountName = order.Vendor?.name || "";
            const createdDate = UserService.parseLastUpdated(order.created_at);

            if (order.Order_Items && order.Order_Items.length > 0) {
                let orderTotal = 0;

                for (const item of order.Order_Items) {
                    const qty = item.qty_pending || item.qty || 0;

                    const itemTotal = qty * (item.net_price || 0);
                    orderTotal += itemTotal;
                    grandTotalItems += qty;

                    byOrderWs.addRow({
                        order_name: orderName,
                        account: accountName,
                        created_date: createdDate,
                        sku: item.Catalog_Item?.sku || "",
                        product: item.Catalog_Item?.name || "",
                        qty,
                        price: item.net_price || 0,
                        total: itemTotal,
                    });
                }

                grandTotalValue += orderTotal;
            }
        }

        // Add summary row
        byOrderWs.addRow({});
        byOrderWs.addRow({
            order_name: "TOTAL",
            account: "",
            created_date: "",
            sku: "",
            product: "",
            qty: grandTotalItems,
            price: "",
            total: grandTotalValue,
        });

        // Format columns
        byOrderWs.getColumn(6).numFmt = "#,##0.00";
        byOrderWs.getColumn(7).numFmt = "$#,##0.00";
        byOrderWs.getColumn(8).numFmt = "$#,##0.00";

        // Summation Tab
        const summationWs = workbook.addWorksheet("Summation");
        summationWs.columns = [
            { header: "SKU", key: "sku" },
            { header: "Product", key: "product" },
            { header: "Total Qty", key: "total_qty" },
            { header: "Total Value", key: "total_value" },
            { header: "Orders", key: "orders" },
        ];

        const summHeaderCells = ["A1", "B1", "C1", "D1", "E1"];
        for (const cell of summHeaderCells) {
            summationWs.getCell(cell).font = { bold: true };
        }

        for (const item of groupedData) {
            const ordersList = item.orders.map((o) => `${o.orderName} (${o.vendorName}): ${o.qty}`).join("; ");

            summationWs.addRow({
                sku: item.sku,
                product: item.name,
                total_qty: item.totalQty,
                total_value: item.totalValue,
                orders: ordersList,
            });
        }

        // Add summary row
        summationWs.addRow({});
        summationWs.addRow({
            sku: "TOTAL",
            product: "",
            total_qty: grandTotalItems,
            total_value: grandTotalValue,
            orders: "",
        });

        // Format columns
        summationWs.getColumn(3).numFmt = "#,##0.00";
        summationWs.getColumn(4).numFmt = "$#,##0.00";
        summationWs.getColumn(5).width = 50;

        // Write file
        const periodStart = moment(props.processedReport.period_start).format("YYYY-MM-DD");
        const periodEnd = moment(props.processedReport.period_end).format("YYYY-MM-DD");

        workbook.xlsx.writeBuffer().then((data) => {
            const blob = new Blob([data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
            const url = window.URL.createObjectURL(blob);
            const anchor = document.createElement("a");
            anchor.href = url;
            anchor.download = `${periodStart}-${periodEnd}-orderwerks-backorder-report.xlsx`;
            anchor.click();
            window.URL.revokeObjectURL(url);
        });
    };

    const renderByOrderView = () => {
        if (!reportData) return null;

        return (
            <div>
                {reportData.orders.map((order) => (
                    <div key={order.guid} className={"card mb-4 p-3 " + (UIService.isMainViewLight() ? "" : "bg-dark text-light")}>
                        <div className="row mb-2">
                            <div className="col-md-3">
                                <OwLabel title={"order"} isBold={true} className="me-2" />
                                <Link to={`/orders/${order.guid}/entry`}>{order.name}</Link>
                            </div>
                            <div className="col-md-3">
                                <OwLabel title={"account"} isBold={true} className="me-2" />
                                {order.Vendor?.name}
                            </div>
                            <div className="col-md-3">
                                <OwLabel title={"created_at"} isBold={true} className="me-2" />
                                {UserService.parseLastUpdated(order.created_at)}
                            </div>
                            <div className="col-md-3 text-end">
                                <OwLabel title={"order_total"} isBold={true} className="me-2" />
                                <NumericFormat
                                    value={
                                        order.Order_Items?.reduce((sum, item) => {
                                            const qty = item.qty_pending || item.qty || 0;
                                            return sum + qty * (item.net_price || 0);
                                        }, 0) || 0
                                    }
                                    displayType={"text"}
                                    decimalScale={supplierModel.pricing_precision || 2}
                                    fixedDecimalScale={true}
                                    thousandSeparator={true}
                                    prefix="$"
                                />
                            </div>
                        </div>

                        <hr />

                        <div className="row">
                            <div className="col">
                                <div className="row mb-2">
                                    <div className="col-3">
                                        <OwLabel title={"sku"} isBold={true} isUnderline={true} />
                                    </div>
                                    <div className="col-3">
                                        <OwLabel title={"product"} isBold={true} isUnderline={true} />
                                    </div>
                                    <div className="col-2 text-end">
                                        <OwLabel title={"qty"} isBold={true} isUnderline={true} />
                                    </div>
                                    <div className="col-2 text-end">
                                        <OwLabel title={"price"} isBold={true} isUnderline={true} />
                                    </div>
                                    <div className="col-2 text-end">
                                        <OwLabel title={"total"} isBold={true} isUnderline={true} />
                                    </div>
                                </div>
                                {order.Order_Items?.map((item, index) => {
                                    const qty = item.qty_pending || item.qty || 0;

                                    return (
                                        <div key={index} className="row mb-1">
                                            <div className="col-3">{item.Catalog_Item?.sku}</div>
                                            <div className="col-3">{item.Catalog_Item?.name}</div>
                                            <div className="col-2 text-end">
                                                <NumericFormat
                                                    value={qty}
                                                    displayType={"text"}
                                                    decimalScale={supplierModel.qty_precision || 2}
                                                    fixedDecimalScale={true}
                                                    thousandSeparator={true}
                                                />
                                            </div>
                                            <div className="col-2 text-end">
                                                <NumericFormat
                                                    value={item.total}
                                                    displayType={"text"}
                                                    decimalScale={supplierModel.pricing_precision || 2}
                                                    fixedDecimalScale={true}
                                                    thousandSeparator={true}
                                                    prefix="$"
                                                />
                                            </div>
                                            <div className="col-2 text-end">
                                                <NumericFormat
                                                    value={qty * (item.net_price || 0)}
                                                    displayType={"text"}
                                                    decimalScale={supplierModel.pricing_precision || 2}
                                                    fixedDecimalScale={true}
                                                    thousandSeparator={true}
                                                    prefix="$"
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const renderSummationView = () => {
        return (
            <div>
                <OwTable
                    columns={getGroupedTableCols()}
                    dataSet={groupedData}
                    userModel={userModel}
                    supplierModel={supplierModel}
                    dataFetch={false}
                    initialData={false}
                />

                <div className={"card mt-4 p-3 " + (UIService.isMainViewLight() ? "" : "bg-dark text-light")}>
                    <h5 className="mb-3">
                        <OwLabel title={"order_details"} isBold={true} />
                    </h5>
                    {groupedData.map((item, index) => (
                        <div key={index} className="mb-3">
                            <div className="row">
                                <div className="col-12">
                                    <strong>
                                        {item.sku} - {item.name}
                                    </strong>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-12 ps-4">
                                    {item.orders.map((order, orderIndex) => (
                                        <div key={orderIndex}>
                                            <Link to={`/orders/${order.orderGuid}/entry`}>{order.orderName}</Link> ({order.vendorName}):
                                            <NumericFormat
                                                value={order.qty}
                                                displayType={"text"}
                                                decimalScale={supplierModel.qty_precision || 2}
                                                fixedDecimalScale={true}
                                                thousandSeparator={true}
                                                className="ms-2"
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div className="BackorderReport">
            <OwTitle title={"backorder_report"} tag={"h3"} />

            <div className="row mb-3">
                <div className="col-md-6">
                    <ControlBar
                        name={"Backorder Report"}
                        sections={[
                            {
                                name: "View",
                                elements: [
                                    <OwButton
                                        key="view-button"
                                        title={viewType === "byOrder" ? "view_summation" : "view_by_order"}
                                        leftIconClass="fas fa-exchange-alt"
                                        fn={() => setViewType(viewType === "byOrder" ? "summation" : "byOrder")}
                                        className=""
                                    />,
                                ],
                            },
                            {
                                name: "Download",
                                elements: [
                                    <OwButton key="download-button" title={"excel"} leftIconClass={"fas fa-download"} fn={downloadExcel} />,
                                ],
                            },
                        ]}
                    />
                </div>
                <div className="col-md-6 text-end">
                    <OwLabel title={"total_backorders"} className="me-2" />
                    {reportData?.totalBackorders || 0}
                    <OwLabel title={"total_items"} className="ms-4 me-2" />
                    <NumericFormat value={totalItems} thousandSeparator={true} displayType={"text"} />
                    <OwLabel title={"total_value"} className="ms-4 me-2" />
                    <NumericFormat
                        value={reportData?.totalValue || 0}
                        thousandSeparator={true}
                        fixedDecimalScale={true}
                        decimalScale={supplierModel.pricing_precision || 2}
                        displayType={"text"}
                        prefix="$"
                    />
                </div>
            </div>

            {viewType === "byOrder" ? renderByOrderView() : renderSummationView()}
        </div>
    );
};
